/**
 * 帳票作成（スナップショット）API（/api/report-snapshots。担当: G4）
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type SnapshotType = '参加船名簿' | '監視実績報告書1' | '監視実績報告書2' | '監視実績報告書3' | '調査船出動計画表';

export interface SnapshotListItem {
  id: number;
  reportType: SnapshotType;
  fiscalYear: number;
  businessOperatorId: number;
  businessOperatorName: string;
  periodFrom: string | null;
  periodTo: string | null;
  baseDate: string | null;
  variant: string | null;
  criteria: Record<string, unknown>;
  header: Record<string, unknown>;
  lineCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface SnapshotDetail extends Omit<SnapshotListItem, 'lineCount'> {
  businessOperatorFormalName: string | null;
  sections: Record<string, Record<string, unknown>[]>;
}

export interface SnapshotFilter {
  businessOperatorId?: number | null;
  from?: string;
  to?: string;
  baseDate?: string;
  variant?: string;
}

export interface GenerateInput {
  type: SnapshotType;
  fiscalYear: number;
  businessOperatorId: number | null;
  periodFrom?: string | null;
  periodTo?: string | null;
  baseDate?: string | null;
  variant?: string | null;
  excludeVesselIds?: number[];
}

export const snapshotKeys = {
  all: ['forms'] as const,
  list: (type: SnapshotType, year: number, f: SnapshotFilter) => [...snapshotKeys.all, 'list', type, year, f] as const,
  detail: (id: number) => [...snapshotKeys.all, 'detail', id] as const,
};

export function useSnapshots(type: SnapshotType, year: number, f: SnapshotFilter) {
  return useQuery({
    queryKey: snapshotKeys.list(type, year, f),
    queryFn: ({ signal }) => api.get<{ items: SnapshotListItem[] }>('/report-snapshots', { type, fiscalYear: year, ...f }, { signal }),
    placeholderData: keepPreviousData,
  });
}

export function useSnapshot(id: number | null) {
  return useQuery({
    queryKey: snapshotKeys.detail(id ?? 0),
    queryFn: ({ signal }) => api.get<SnapshotDetail>(`/report-snapshots/${id}`, undefined, { signal }),
    enabled: id != null,
  });
}

export function useGenerateSnapshot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GenerateInput) => api.post<{ id: number }>('/report-snapshots/generate', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: snapshotKeys.all }),
  });
}

export function useUpdateSnapshotHeader(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (header: Record<string, unknown>) => api.put<SnapshotDetail>(`/report-snapshots/${id}`, { header }),
    onSuccess: (data) => {
      qc.setQueryData(snapshotKeys.detail(id), data);
      return qc.invalidateQueries({ queryKey: snapshotKeys.all });
    },
  });
}

export function useDeleteSnapshot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/report-snapshots/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: snapshotKeys.all }),
  });
}
